import Button from '@anm/components/buttons/Button/Button';
import FieldWithButton, {
  FieldWithButtonOptionalProps
} from '@anm/components/form/FieldWithButton';
import noop from '@anm/helpers/noop';
import { FC } from 'react';
import { branch, renderComponent } from 'recompose';

import useCopyLink from './hooks';

export type CopyLinkDirection = 'vertical' | 'horizontal';

export type CopyLinkProps = FieldWithButtonOptionalProps & {
  link: string;
  disabled?: boolean;
  multirows?: boolean;
  withField?: boolean;
  buttonText?: string;
  autoResize?: boolean;
  isFieldClickable?: boolean;
  onCopy?(): void;
};

export type HOCs = IsCopiedState & {};

type IsCopiedState = {
  isCopied: boolean;
  setIsCopied: (value: boolean) => boolean;
};

const CopyLinkFieldWithButton: FC<HOCs & CopyLinkProps> = ({
  link,
  disabled = false,
  multirows = false,
  buttonText = 'Copy Link',
  onCopy = noop,
  ...otherProps
}) => {
  const { handleClick, copyLinkButtonText, linkToCopy } = useCopyLink({
    link,
    buttonText
  });

  const handleCopy = () => {
    handleClick();
    onCopy();
  };

  return (
    <FieldWithButton
      value={linkToCopy}
      multirows={multirows}
      onClick={handleCopy}
      disabled={disabled}
      {...otherProps}
    >
      {copyLinkButtonText}
    </FieldWithButton>
  );
};

const CopyLinkButton: FC<HOCs & CopyLinkProps> = ({
  link,
  buttonText,
  disabled = false,
  buttonSize = 'large',
  buttonVariant = 'light-blue-lowercase-text-field'
}) => {
  const { handleClick, copyLinkButtonText } = useCopyLink({
    link,
    buttonText
  });

  return (
    <Button
      size={buttonSize}
      variant={buttonVariant}
      onClick={handleClick}
      disabled={disabled}
    >
      {copyLinkButtonText}
    </Button>
  );
};

const isFieldWithButton = ({ withField = true }: CopyLinkProps) => withField;

export default branch(
  isFieldWithButton,
  renderComponent(CopyLinkFieldWithButton)
)(CopyLinkButton);
